import { formatNumberUSD, formatNumberWithHumanReadableUnit } from "util/format"

import { MarketExchangeFeeRatios } from "@perp/sdk-curie"
import { useAmmStatistics } from "container/service/StatisticsServiceContainer/useAmmStatistics"
import { useMemo, useState } from "react"
import { usePerpInitializedEffect } from "sdk-react/perpetualProtocol/usePerpInitializedEffect"
import { usePool } from "sdk-react/pools/usePool"

const PLACEHOLDER = "-,---"

interface UsePoolInitialState {
    tickerSymbol: string
}

export const usePoolStats = ({ tickerSymbol }: UsePoolInitialState) => {
    const { pool, isLoading, indexPrice } = usePool({ tickerSymbol })
    const { volume24h } = useAmmStatistics({ tickerSymbol })

    const [exchangeFeeRatios, setExchangeFeeRatios] = useState<MarketExchangeFeeRatios>()
    // NOTE: this `insuranceFundFeeRatios` is used to calculate the cut from trading fee
    // const [insuranceFundFeeRatios, setInsuranceFundFeeRatios] = useState<MarketInsuranceFundFeeRatios>()

    usePerpInitializedEffect(({ clearingHouseConfig }) => {
        setExchangeFeeRatios(clearingHouseConfig.marketExchangeFeeRatios)
    }, [])

    const tvl = useMemo(() => {
        if (!pool || !indexPrice) {
            return
        }
        return pool.baseAmount.mul(indexPrice).add(pool.quoteAmount)
    }, [indexPrice, pool])

    const tradingFee24h = useMemo(() => {
        if (!volume24h || !exchangeFeeRatios || !pool) {
            return
        }
        return volume24h.mul(exchangeFeeRatios[pool.baseAddress])
    }, [exchangeFeeRatios, pool, volume24h])

    return {
        isLoading,
        ...pool,
        tvl,
        volume24h,
        tradingFee24h,
        tvlStr: tvl ? formatNumberWithHumanReadableUnit(tvl) : PLACEHOLDER,
        volume24hStr: volume24h ? formatNumberWithHumanReadableUnit(volume24h) : PLACEHOLDER,
        tradingFee24hStr: tradingFee24h ? formatNumberUSD({ num: tradingFee24h, displayLength: 4 }) : PLACEHOLDER,
    }
}
